/* @flow */
import * as React from 'react';
import _ from 'lodash';

import DeliveryModalSectionItem from './DeliveryModalSectionItem';
import DeliveryModalSectionSubtext from './DeliveryModalSectionSubtext';
import Divider from 'components/Divider';
import Glyph from 'components/Glyph';
import Heading from 'components/Heading';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import ReadReceipt from 'chat_v2/components/ReadReceipt';
import Row from 'components/Row';
import Text from 'components/Text';
import TextWithGlyph from 'components/TextWithGlyph';

import type {
  fullPreciseSummaryEntry_users,
  fullPreciseSummaryEntry_unidentifiable,
} from 'chat/fragments/__generated__/fullPreciseSummaryEntry';

import './styles/DeliveryModalSection.scss';

type Props = {
  title: string,
  count: number,
  status: string,
  users: ?$ReadOnlyArray<fullPreciseSummaryEntry_users>,
  unidentifiables?: ?$ReadOnlyArray<fullPreciseSummaryEntry_unidentifiable>,
  percent?: number,
  isEstimate: boolean,
  onSectionFold?: Function,
  onSectionUnfold?: Function,
  onRequestCSV?: ?() => void,
  downloadAvailable?: ?boolean,
  downloadAvailableUntil?: ?Date,
};

type State = {
  folded: boolean,
};

export class DeliveryModalSection extends React.Component<Props, State> {
  state: State = {
    folded: true,
  };

  toggleFold = () => {
    if (this.state.folded) {
      this.setState({ folded: false }, () => {
        if (this.props.onSectionUnfold) {
          this.props.onSectionUnfold();
        }
      });
    } else {
      this.setState({ folded: true }, () => {
        if (this.props.onSectionFold) {
          this.props.onSectionFold();
        }
      });
    }
  };

  canUnfold(): boolean {
    const { users } = this.props;
    return _.isEmpty(users) === false && !this.props.isEstimate;
  }

  foldIcon() {
    if (_.isEmpty(this.props.users) && _.isEmpty(this.props.unidentifiables)) {
      return null;
    }

    return this.state.folded ? (
      <Glyph solo size="small" color="mascot" name="chevron-down" />
    ) : (
      <Glyph solo size="small" color="mascot" name="chevron-up" />
    );
  }

  renderSectionHeader = () => {
    return (
      <div className="DeliveryModalSection-header">
        <div>
          <div>
            <Heading level="3">
              <MediaObject gutter="xsmall" center>
                <Text>{this.props.title}</Text>
                <Link data-test="fold-icon" onClick={this.toggleFold}>
                  {this.foldIcon()}
                </Link>
              </MediaObject>
            </Heading>
          </div>
          <div>
            <Text color="graphite" size="xxsmall">
              <DeliveryModalSectionSubtext
                status={this.props.status}
                count={this.props.count}
                isEstimate={this.props.isEstimate}
              />
            </Text>
          </div>
        </div>
        {!isNaN(this.props.percent) && (
          <div>
            <MediaObject gutter="xsmall" center>
              <ReadReceipt iconSize="large" glyphDisplay status={this.props.status} />
              <Text size="xlarge">{this.props.percent}%</Text>
            </MediaObject>
          </div>
        )}
      </div>
    );
  };

  renderExpiredCSVDownload = () => {
    const { folded } = this.state;
    const expiredCSV = (
      <span data-test="expired-csv-download">
        <TextWithGlyph
          color="graphite"
          glyphColor="graphite"
          weight="semibold"
          glyph="spreadsheet"
          gutter="xsmall"
          size="xxsmall">
          Download list (CSV file) unavailable after 30 days
        </TextWithGlyph>
      </span>
    );
    return folded ? (
      expiredCSV
    ) : (
      <React.Fragment>
        <Row size="small">{expiredCSV}</Row>
        <Divider size="flush" />
      </React.Fragment>
    );
  };

  renderCSVDownload = () => {
    const { status } = this.props;
    const { folded } = this.state;
    const statusToUserTypes = {
      delivery_error: 'errored_users',
      unreachable: 'unreachable_users',
    };
    const csvLink = (
      <MediaObject figure="right" center data-test="csv-download">
        <Link
          eventKey={`delivery_summary.${statusToUserTypes[status]}.request_csv.click`}
          onClick={this.props.onRequestCSV}>
          <TextWithGlyph weight="semibold" glyph="spreadsheet" gutter="xsmall" size="xxsmall">
            Download list (CSV file)
          </TextWithGlyph>
        </Link>
        {this.props.downloadAvailableUntil ? (
          <Text
            size="xxsmall"
            color="graphite">{`Available until ${this.props.downloadAvailableUntil.toString()}`}</Text>
        ) : (
          <span />
        )}
      </MediaObject>
    );
    return folded ? (
      csvLink
    ) : (
      <React.Fragment>
        <Row size="small">{csvLink}</Row>
        <Divider size="flush" />
      </React.Fragment>
    );
  };

  renderDownload = () => {
    const { downloadAvailable } = this.props;
    if (downloadAvailable) {
      return this.renderCSVDownload();
    } else if (downloadAvailable === false) {
      return this.renderExpiredCSVDownload();
    }
    return null;
  };

  render() {
    const { users, unidentifiables } = this.props;
    const sectionHeaderWithCSV = (
      <React.Fragment>
        <Row size="xsmall">{this.renderSectionHeader()}</Row>
        <div data-test="download-container">{this.renderDownload()}</div>
      </React.Fragment>
    );
    return (
      <div className="DeliveryModalSection">
        {this.state.folded &&
          (this.props.isEstimate ? sectionHeaderWithCSV : this.renderSectionHeader())}
        {!this.state.folded && (
          <React.Fragment>
            {sectionHeaderWithCSV}
            {users &&
              users.map(user => (
                <DeliveryModalSectionItem
                  key={user.uuid}
                  user={user}
                  avatarLabel={user.name}
                  deliveryError={user.deliveryError}
                />
              ))}
            {unidentifiables &&
              unidentifiables.map((unidentifiable, index) => (
                <DeliveryModalSectionItem
                  key={unidentifiable.studentId || index}
                  user={undefined}
                  avatarLabel={unidentifiable.studentId || 'Unidentifiable'}
                  deliveryError={{
                    code: unidentifiable.reason,
                    message: unidentifiable.message,
                  }}
                />
              ))}
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default DeliveryModalSection;
